"use client";

import { Slider as SliderPrimitive } from "@base-ui/react/slider";
import * as React from "react";
import { cn } from "@/lib/utils";

type SliderProps = Omit<
  SliderPrimitive.Root.Props<readonly number[]>,
  "minStepsBetweenValues" | "onValueChange" | "onValueCommitted"
> & {
  minStepsBetweenThumbs?: number;
  minStepsBetweenValues?: number;
  onValueChange?: (
    value: number[],
    eventDetails: SliderPrimitive.Root.ChangeEventDetails,
  ) => void;
  onValueCommit?: (
    value: number[],
    eventDetails: SliderPrimitive.Root.CommitEventDetails,
  ) => void;
  onValueCommitted?: (
    value: number[],
    eventDetails: SliderPrimitive.Root.CommitEventDetails,
  ) => void;
};

function normalizeSliderValue(value: readonly number[] | number) {
  return Array.isArray(value) ? Array.from(value) : [value];
}

function Slider({
  className,
  defaultValue,
  max = 100,
  min = 0,
  minStepsBetweenThumbs,
  minStepsBetweenValues,
  onValueChange,
  onValueCommit,
  onValueCommitted,
  value,
  ...props
}: SliderProps) {
  const values = React.useMemo(
    () =>
      Array.isArray(value)
        ? value
        : Array.isArray(defaultValue)
          ? defaultValue
          : [min, max],
    [defaultValue, max, min, value],
  );

  return (
    <SliderPrimitive.Root
      className={cn(
        "relative flex w-full touch-none items-center select-none data-[orientation=vertical]:h-full data-[orientation=vertical]:min-h-44 data-[orientation=vertical]:w-auto data-[orientation=vertical]:flex-col data-disabled:opacity-50",
        className,
      )}
      data-slot="slider"
      defaultValue={defaultValue}
      max={max}
      min={min}
      minStepsBetweenValues={minStepsBetweenValues ?? minStepsBetweenThumbs}
      onValueChange={(nextValue, eventDetails) => {
        onValueChange?.(normalizeSliderValue(nextValue), eventDetails);
      }}
      onValueCommitted={(nextValue, eventDetails) => {
        const normalizedValue = normalizeSliderValue(nextValue);
        onValueCommit?.(normalizedValue, eventDetails);
        onValueCommitted?.(normalizedValue, eventDetails);
      }}
      thumbAlignment="edge"
      thumbCollisionBehavior="none"
      value={value}
      {...props}
    >
      <SliderPrimitive.Control className="relative flex w-full grow items-center data-[orientation=vertical]:h-full data-[orientation=vertical]:w-auto data-[orientation=vertical]:flex-col">
        <SliderPrimitive.Track
          className={cn(
            "relative grow overflow-hidden rounded-full bg-muted data-[orientation=horizontal]:h-1.5 data-[orientation=horizontal]:w-full data-[orientation=vertical]:h-full data-[orientation=vertical]:w-1.5",
          )}
          data-slot="slider-track"
        >
          <SliderPrimitive.Indicator
            className="absolute bg-primary data-[orientation=horizontal]:h-full data-[orientation=vertical]:w-full"
            data-slot="slider-range"
          />
        </SliderPrimitive.Track>
        {values.map((_, index) => (
          <SliderPrimitive.Thumb
            className="block size-4 shrink-0 rounded-full border border-primary bg-background shadow-sm transition-[color,box-shadow] hover:ring-4 hover:ring-ring/20 focus-visible:ring-4 focus-visible:ring-ring/50 focus-visible:outline-hidden disabled:pointer-events-none disabled:opacity-50"
            data-slot="slider-thumb"
            index={index}
            // biome-ignore lint/suspicious/noArrayIndexKey: slider thumbs are positional controls.
            key={index}
          />
        ))}
      </SliderPrimitive.Control>
    </SliderPrimitive.Root>
  );
}

export { Slider };
